'use client'

import { useMemo } from 'react'
import { CommentData } from '@liveblocks/client'

import { useAuth } from '@/dc-shared-web/src'

import { UserThreadNotification } from '@/app/core/providers/UserNotificationProvider'

import NotificationTitle from '@/app/core/components/notification/NotificationTitle'
import NotificationComment from '@/app/core/components/notification/NotificationComment'
import NotificationaUserAvatars from '@/app/core/components/notification/NotificationUserAvatars'

import {
  isCommentUnread,
  getNotificationUrl,
  isCommentMentionningUser,
} from '@/app/core/components/notification/utils'

import useNotificationMenu from '@/app/core/components/notification/hooks/useNotificationMenu'
import NotificationMenu from '@/app/core/components/notification/components/NotificationMenu'
import NotificationContainer from '@/app/core/components/notification/components/NotificationContainer'

const getInvolvedUsersRelevantComments = (
  comments: CommentData[],
  unreadComments: CommentData[],
  currentUserUuuid: string,
  isMentionNotification: boolean
): CommentData[] => {
  // If the notification is not a mention, we want all the comments from users
  // that are not the current user
  if (!isMentionNotification) {
    return comments.filter((comment) => comment.userId !== currentUserUuuid)
  }

  // If the notification is a mention, we want all the comments that are mentioning
  // the current user, but not created by the current user
  // If there are unread comments, we also limit the comments to the unread ones
  return (unreadComments.length > 0 ? unreadComments : comments).filter(
    (comment) =>
      comment.userId !== currentUserUuuid &&
      isCommentMentionningUser(comment, currentUserUuuid)
  )
}

// Involved users are:
// - mention notification && unread comments: all users mentioning the current user in the unread comments
// - mention notification && no unread comments: all users mentioning the current user
// - no mention notification && unread comments: all users that commented
const getInvolvedUsers = (
  comments: CommentData[],
  unreadComments: CommentData[],
  currentUserUuuid: string,
  isMentionNotification: boolean
) => {
  const relevantComments = getInvolvedUsersRelevantComments(
    comments,
    unreadComments,
    currentUserUuuid,
    isMentionNotification
  )
  const latestCommentTimeByUserId = new Map<string, number>()

  for (const comment of relevantComments) {
    const currentCommentTime = new Date(comment.createdAt).getTime()
    const latestUserCommentTime = latestCommentTimeByUserId.get(comment.userId)

    if (!latestUserCommentTime || currentCommentTime > latestUserCommentTime) {
      latestCommentTimeByUserId.set(comment.userId, currentCommentTime)
    }
  }

  return Array.from(latestCommentTimeByUserId.keys()).sort(
    (a, b) =>
      latestCommentTimeByUserId.get(b)! - latestCommentTimeByUserId.get(a)!
  )
}

const useThreadNotificationDetails = (
  notification: UserThreadNotification,
  currentUserUuuid: string
) =>
  useMemo(() => {
    const [, crunchUuid, surveyUuid] = notification.roomId?.split(':') || []
    const comments =
      notification.thread?.comments
        .filter((comment) => Boolean(!comment.deletedAt && !!comment.body))
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()) ?? []
    const unreadComments = comments.filter((comment) =>
      isCommentUnread(comment, notification)
    )
    const isMentionNotification =
      comments.length > 0 &&
      isCommentMentionningUser(comments[comments.length - 1], currentUserUuuid)
    const involvedUsers = getInvolvedUsers(
      comments,
      unreadComments,
      currentUserUuuid,
      isMentionNotification
    )
    const notificationUrl = getNotificationUrl(
      crunchUuid,
      surveyUuid,
      notification.thread,
      comments.length > 0 ? comments[comments.length - 1] : undefined
    )

    return {
      comments,
      hasUnreadComments: unreadComments.length > 0,
      isMentionNotification,
      involvedUsers,
      notificationUrl,
    }
  }, [notification, currentUserUuuid])

export default function ThreadNotification({
  notification,
  onClick,
}: {
  notification: UserThreadNotification
  onClick: () => void
}) {
  const { user } = useAuth()

  const {
    comments,
    hasUnreadComments,
    isMentionNotification,
    involvedUsers,
    notificationUrl,
  } = useThreadNotificationDetails(notification, user.uuid)

  const {
    isHovered,
    isMenuOpened,
    setIsMenuOpened,
    onMarkAsRead,
    onDelete,
    onMouseEnter,
    onMouseLeave,
  } = useNotificationMenu(notification.id)

  return (
    <NotificationContainer
      isUnread={hasUnreadComments}
      notificationUrl={notificationUrl}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <NotificationaUserAvatars involvedUsers={involvedUsers} />
      <div
        className='tw-flex tw-flex-col tw-gap-2 tw-grow tw-overflow-hidden'
        data-testid={`notification-item`}
      >
        <div className='tw-flex tw-gap-2'>
          <NotificationTitle
            involvedUsers={involvedUsers}
            isMentionNotification={isMentionNotification}
            roomId={notification.roomId ?? ''}
          />
          <NotificationMenu
            notificationId={notification.id}
            isUnread={hasUnreadComments}
            isHovered={isHovered}
            isMenuOpened={isMenuOpened}
            setIsMenuOpened={setIsMenuOpened}
            date={new Date(notification.notifiedAt || Date.now())}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
          />
        </div>
        {comments.length > 0 && <NotificationComment comment={comments[0]} />}
        {comments.length > 3 && (
          <div>
            <span className='tw-text-primary-600 tw-text-sm'>{`+${comments.length - 3} updates to this thread`}</span>
          </div>
        )}
        {(comments.length > 3 ? comments.slice(-2) : comments.slice(1)).map(
          (comment) => (
            <NotificationComment key={comment.id} comment={comment} indent />
          )
        )}
      </div>
    </NotificationContainer>
  )
}
